import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiCall, localShell } from '../utils/api.js'

// Livello minimo delle API locali che il frontend si aspetta (vedi ConfigController.ApiLevel nel
// LocalBackend). L'exe gira sul PC di ogni utente, lanciato dalla cartella OneDrive condivisa, e può
// restare indietro rispetto al sito: sotto questo livello si avvisa, e chi salva file ripiega su un
// comportamento compatibile.
export const MIN_LOCAL_API_LEVEL = 2

export const useLocalBackendStore = defineStore('localBackend', () => {
  // Le API locali esistono solo dentro la shell desktop (LexSystem.exe): nel browser puro non c'è
  // nulla da chiamare, e le funzioni sulle cartelle restano spente senza che sia un errore.
  const hasShell = !!localShell
  const shellVersion = localShell?.version || null

  const isConnected = ref(false)
  const isChecking = ref(false)
  const configurationError = ref(null)
  const missingPaths = ref([])
  const warnings = ref([])
  // null = non ancora saputo (API locali irraggiungibili o non ancora chieste)
  const apiLevel = ref(null)
  const version = ref(null)

  const isOutdated = computed(() => apiLevel.value != null && apiLevel.value < MIN_LOCAL_API_LEVEL)

  // Le installazioni precedenti all'endpoint rispondono 404: livello 1. Un altro errore (rete)
  // lascia il livello sconosciuto, così un exe spento non passa per vecchio.
  async function checkVersion() {
    try {
      const info = await apiCall('/api/config/version', {
        method: 'GET',
        backend: 'local',
        includeAuth: false
      })
      apiLevel.value = Number(info?.apiLevel) || 1
      version.value = info?.version || null
    } catch (error) {
      if (error?.response?.status === 404) {
        apiLevel.value = 1
        version.value = null
      }
    }
    return apiLevel.value
  }

  // Per chi deve decidere come salvare: chiede solo la prima volta.
  async function ensureApiLevel() {
    if (!hasShell) return null
    return apiLevel.value ?? await checkVersion()
  }

  async function checkConnection() {
    if (!hasShell) {
      isConnected.value = false
      configurationError.value = null
      missingPaths.value = []
      warnings.value = []
      return false
    }

    isChecking.value = true
    configurationError.value = null
    missingPaths.value = []
    warnings.value = []

    try {
      // Verifica connessione e valida configurazione
      const validation = await apiCall('/api/config/config/validate', {
        method: 'GET',
        backend: 'local',
        includeAuth: false
      })

      await checkVersion()

      // Se la configurazione non è valida, disabilita le funzionalità
      if (!validation.isValid) {
        configurationError.value = 'Configuration paths are invalid or missing'
        missingPaths.value = validation.missingPaths || []
        isConnected.value = false
        return false
      }

      // Salva eventuali warnings (template o archive path mancanti)
      if (validation.warnings && validation.warnings.length > 0) {
        warnings.value = validation.warnings
      }

      isConnected.value = true
      return true
    } catch (error) {
      isConnected.value = false
      configurationError.value = 'LocalBackend not available'
      return false
    } finally {
      isChecking.value = false
    }
  }

  return {
    hasShell,
    shellVersion,
    isConnected,
    isChecking,
    configurationError,
    missingPaths,
    warnings,
    apiLevel,
    version,
    isOutdated,
    checkConnection,
    checkVersion,
    ensureApiLevel
  }
})
